import type { Ref } from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import {
  HANDLEBARS_LANGUAGE,
  PLAINTEXT_LANGUAGE,
  type EditorCompletion,
} from '../notifications/editorLanguages';
import { formatHandlebarsHtml } from '../notifications/handlebarsFormat';

/**
 * Monaco and its HTML language service are ~1.5 MB, and this component is reachable from the
 * shared component barrel — so importing `monacoSetup` statically would put all of it in the
 * entry bundle for every page. Reached through `import()` instead, it is a chunk that loads when
 * somebody opens a template.
 */
const loadMonacoSetup = () => import('../notifications/monacoSetup');

/**
 * Handlebars template editor, on Monaco (AU-69).
 *
 * <p>It replaces a hand-rolled control — a transparent `textarea` over a regex-highlighted
 * `pre`, with a line-number gutter — that could not soft-wrap, because the gutter had to stay
 * aligned row for row. Against a body stored as one line, which every seeded body was, that
 * rendered as a single endlessly scrolling row. Monaco brings wrapping, folding, find/replace,
 * bracket matching and a completion list of the notification's real tokens.</p>
 */
export interface TemplateEditorHandle {
  /** Inserts at the caret, replacing any selection — how the token palette adds a token. */
  insertAtCursor: (text: string) => void;
  /** Runs the Format action, as the toolbar button and the keybinding do. */
  format: () => void;
}

export interface TemplateEditorProps {
  /** Handlebars source. */
  value: string;
  onChange: (value: string) => void;
  /** Editor height in px (SMS bodies use a shorter surface). */
  minHeight?: number;
  disabled?: boolean;
  /** Accessible label for the code area (translated copy). */
  ariaLabel?: string;
  /**
   * SMS bodies are plain text, so they get no HTML language, no formatting and no wrapping
   * ruler — only the token completions.
   */
  plainText?: boolean;
  /** Tokens and helpers to offer while typing, from the server's metadata endpoint. */
  completions?: EditorCompletion[];
  /** Told when the editor is ready, so a panel can gate its Format button. */
  onReadyChange?: (ready: boolean) => void;
  handle?: Ref<TemplateEditorHandle>;
}

export function TemplateEditor({
  value,
  onChange,
  minHeight = 280,
  disabled = false,
  ariaLabel,
  plainText = false,
  completions,
  onReadyChange,
  handle,
}: TemplateEditorProps) {
  const theme = useTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  // Whether the author has ever put the caret in the editor. hasTextFocus() cannot answer this:
  // clicking a palette chip moves focus to the button, so it reads false even mid-session.
  const everFocusedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The provider reads from a module-level list, so the editor's suggestions follow whichever
  // notification code the panel is showing without re-registering a provider per mount.
  useEffect(() => {
    void loadMonacoSetup().then(({ setEditorCompletions }) => {
      setEditorCompletions(completions ?? []);
    });
  }, [completions]);

  useEffect(() => {
    let cancelled = false;
    void loadMonacoSetup()
      .then(({ setupMonaco }) => setupMonaco())
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((error: unknown) => {
        // Without this the editor spins forever on a chunk that failed to load, with nothing in
        // the console and no way for the author to tell the difference from a slow network.
        if (!cancelled) setLoadError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const format = useCallback(() => {
    const instance = editorRef.current;
    if (!instance || plainText) return;

    const current = instance.getValue();
    const formatted = formatHandlebarsHtml(current);
    if (formatted !== current) {
      // Bracketed by undo stops so Format is exactly one Ctrl+Z, rather than coalescing with
      // whatever the author typed immediately before it.
      instance.pushUndoStop();
      instance.executeEdits('format', [
        { range: instance.getModel()!.getFullModelRange(), text: formatted },
      ]);
      instance.pushUndoStop();
    }
  }, [plainText]);

  useImperativeHandle(
    handle,
    () => ({
      insertAtCursor: (text: string) => {
        const instance = editorRef.current;
        if (!instance) {
          onChange(value + text);
          return;
        }
        // Until the editor has been focused, getSelection() is still Monaco's initial line 1,
        // column 1 — so a chip clicked straight after opening a template inserted before the
        // opening <p> rather than at the end, where the textarea this replaced put it.
        if (!everFocusedRef.current) {
          const model = instance.getModel();
          if (model) {
            instance.setPosition(model.getFullModelRange().getEndPosition());
          }
        }

        const selection = instance.getSelection();
        if (selection) {
          instance.executeEdits('insert-token', [
            { range: selection, text, forceMoveMarkers: true },
          ]);
          instance.pushUndoStop();
        }
        instance.focus();
      },
      format,
    }),
    [format, onChange, value],
  );

  // Formatting a freshly loaded body is the panel's job, not the editor's: routing it through
  // `onChange` here marked a template dirty the moment it was opened, and a mount callback fires
  // once, so a version or language switch never reformatted. The editor formats only on request.
  const onMount: OnMount = (instance) => {
    editorRef.current = instance;
    instance.onDidFocusEditorText(() => {
      everFocusedRef.current = true;
    });
    onReadyChange?.(true);
  };

  // Memoized: `@monaco-editor/react` calls `editor.updateOptions(options)` whenever this
  // object's identity changes, so a fresh literal per render meant a full options reapply on
  // every keystroke.
  const options = useMemo<editor.IStandaloneEditorConstructionOptions>(
    () => ({
      readOnly: disabled,
      ariaLabel,
      // The whole point of the change: long lines wrap instead of scrolling off.
      wordWrap: 'on',
      minimap: { enabled: false },
      lineNumbers: 'on',
      scrollBeyondLastLine: false,
      automaticLayout: true,
      fontSize: 13,
      fontFamily: "ui-monospace, 'Cascadia Mono', Menlo, Consolas, monospace",
      tabSize: 2,
      renderLineHighlight: 'none',
      overviewRulerLanes: 0,
      padding: { top: 12, bottom: 12 },
      suggestOnTriggerCharacters: true,
      quickSuggestions: { other: true, comments: false, strings: true },
    }),
    [disabled, ariaLabel],
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        height: minHeight,
        position: 'relative',
      }}
    >
      {ready ? (
        <Editor
          height={minHeight}
          language={plainText ? PLAINTEXT_LANGUAGE : HANDLEBARS_LANGUAGE}
          theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs'}
          value={value}
          onChange={(next) => onChange(next ?? '')}
          onMount={onMount}
          options={options}
        />
      ) : (
        // Deliberately not labelled with `ariaLabel`: the editor that replaces it carries that
        // name, and two elements answering to it would make "the body field" ambiguous to a
        // screen reader and to anything querying by label.
        <Box
          role="status"
          aria-live="polite"
          sx={{ display: 'grid', placeItems: 'center', height: '100%', p: 2 }}
        >
          {loadError ? (
            <Typography sx={{ fontSize: 13, color: 'error.main', textAlign: 'center' }}>
              {loadError}
            </Typography>
          ) : (
            <CircularProgress size={20} />
          )}
        </Box>
      )}
    </Box>
  );
}
